import { SvgIcon } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect } from 'react';

import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { EmptyCardClassification } from 'src/generated/gql/graphql';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import Colors from 'src/nightingale/Colors';
import { useObservability } from 'src/observability/useObservability';
import { ReactComponent as MedicalIcon } from 'src/shared/icons/iconMedical.svg';
import { ReactComponent as SupportIcon } from 'src/shared/icons/iconSupport.svg';

const useStyles = makeStyles({
  centeringContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',

    '& svg': {
      marginRight: 24,
    },
  },
  textArea: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: Colors.Taupe,

    '& .topLine': {
      fontSize: 16,
      letterSpacing: '0.201px',
    },

    '& .bottomLine': {
      textTransform: 'uppercase',
      fontSize: 12,
      letterSpacing: '1.44px',
    },
  },
});

type EmptyFocusCardProps = {
  type: EmptyCardClassification;
};

const MessageMap = {
  [EmptyCardClassification.AfterEndOfDay]: {
    topLine: 'You’re all done for the day!',
    bottomLine: 'Great job today!',
  },
  [EmptyCardClassification.InsufficientTime]: {
    topLine: 'Your schedule doesn’t have enough time for a visit right now.',
    bottomLine: '',
  },
  [EmptyCardClassification.NoPatientsAvailable]: {
    topLine: 'You’ll see a patient here soon',
    bottomLine: 'No matching patients in line',
  },
  [EmptyCardClassification.PatientBeingPrepped]: {
    topLine: 'A patient is almost done with their prep',
    bottomLine: 'They will be suggested to you soon',
  },
  [EmptyCardClassification.PatientJoiningSoon]: {
    topLine: 'Your next patient is on their way',
    bottomLine: "They'll be ready soon",
  },
  [EmptyCardClassification.Pause]: {
    topLine: 'Take a moment to breathe',
    bottomLine: 'Enjoy this tranquil pause',
  },
};

const UNKNOWN_MESSAGE = {
  topLine: 'Unknown empty card type',
  bottomLine: 'No empty card to display',
};

export const EmptyFocusCard = (props: EmptyFocusCardProps) => {
  const { type: cardType } = props;

  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const sendStaffEvent = useObservability(enableObservability);

  const providerId = useProviderId();

  const classes = useStyles();

  useEffect(() => {
    sendStaffEvent({
      eventType: 'myDayToday:emptyCardViewed',
      userId: providerId ?? null,
      details: {
        emptyCardType: cardType,
      },
    });
  }, [cardType, sendStaffEvent, providerId]);

  const messageText = MessageMap[cardType] || UNKNOWN_MESSAGE;
  const Icon =
    cardType === EmptyCardClassification.PatientBeingPrepped ||
    EmptyCardClassification.PatientJoiningSoon
      ? MedicalIcon
      : SupportIcon;

  return (
    <div className={classes.centeringContainer}>
      <div className={classes.content}>
        <SvgIcon component={Icon} style={{ width: 48, height: 48 }} viewBox="0 0 48 48" />
        <div className={classes.textArea}>
          <div className="topLine">{messageText.topLine}</div>
          <div className="bottomLine">{messageText.bottomLine}</div>
        </div>
      </div>
    </div>
  );
};
